/**
 * Example packs: a first run that is not an empty screen.
 *
 * **These are ordinary user data.** A pack installs through the same
 * repositories as anything hand-made, and produces tracks and templates that
 * are indistinguishable from them afterwards — no flag, no reserved id, no
 * protected status. Every one can be renamed, archived and deleted.
 *
 * This module is where use-case leakage would enter the codebase, so the test
 * is explicit: **nothing here may be referenced from the domain model, and the
 * installer must not know what any pack contains.** If a pack's subject matter
 * would look wrong to someone tracking guitar practice or rainfall, that is
 * fine — it is data. If the *model* would look wrong to them, something has
 * gone into the wrong layer.
 */

import type { Result } from '../model/result.js'
import { err, invalid, ok } from '../model/result.js'
import type { CheckInTemplate } from '../model/template.js'
import type { PaletteColour } from '../model/palette.js'
import type { Track, TrackKind } from '../model/track.js'
import type { Storage } from '../ports/storage.js'
import type { Repositories } from './repositories.js'

export interface PackTrack {
  readonly name: string
  readonly unit?: string
  readonly description?: string
  /**
   * The question this track is asked, when the name is not the question.
   *
   * Absent is normal — `questionFor` derives one from the name (T-810). It is
   * here because the packs are this app's own worked examples, and a feature
   * none of them uses is a feature nobody knows exists.
   */
  readonly question?: string
  readonly colour?: PaletteColour
  readonly kind: TrackKind
}

export interface PackTemplate {
  readonly name: string
  readonly description?: string
  /** Positions in the pack's own track list — real ids do not exist until install. */
  readonly trackIndices: readonly number[]
}

export interface PackDefinition {
  readonly id: string
  readonly name: string
  readonly description: string
  readonly tracks: readonly PackTrack[]
  readonly templates: readonly PackTemplate[]
}

export interface InstalledPack {
  readonly tracks: readonly Track[]
  readonly templates: readonly CheckInTemplate[]
}

/** Set once the first-run offer has been made, so it is not repeated. */
export const FIRST_RUN_KEY = 'firstRun.packsOffered'

/**
 * Installs a pack through the ordinary repositories.
 *
 * No direct storage writes and no special-casing: whatever validation and
 * numbering a hand-made track goes through, these go through too. Adding a new
 * pack is a data change, never a change here.
 */
export async function installPack(
  pack: PackDefinition,
  repos: Repositories,
): Promise<Result<InstalledPack>> {
  const created: Track[] = []

  for (const definition of pack.tracks) {
    const result = await repos.tracks.create({
      name: definition.name,
      kind: definition.kind,
      ...(definition.unit === undefined ? {} : { unit: definition.unit }),
      ...(definition.description === undefined
        ? {}
        : { description: definition.description }),
      ...(definition.question === undefined ? {} : { question: definition.question }),
      ...(definition.colour === undefined ? {} : { colour: definition.colour }),
      // A pack's tracks are listed under the pack's name, which is what makes a
      // second pack installed later read as a separate set rather than a jumble.
      group: pack.name,
    })
    if (!result.ok) {
      return err<InstalledPack>(...result.errors)
    }
    created.push(result.value)
  }

  const templates: CheckInTemplate[] = []
  for (const definition of pack.templates) {
    const trackIds: string[] = []
    for (const index of definition.trackIndices) {
      const track = created[index]
      if (track === undefined) {
        return err<InstalledPack>(
          invalid(
            'pack.template.badIndex',
            `Pack "${pack.id}" references a track it does not define.`,
          ),
        )
      }
      trackIds.push(track.id)
    }

    const result = await repos.templates.create({
      name: definition.name,
      trackIds,
      ...(definition.description === undefined
        ? {}
        : { description: definition.description }),
    })
    if (!result.ok) {
      return err<InstalledPack>(...result.errors)
    }
    templates.push(result.value)
  }

  return ok({ tracks: created, templates })
}

/** Whether the first-run offer has already been made. */
export async function packsOffered(storage: Storage): Promise<boolean> {
  return (await storage.settings.get(FIRST_RUN_KEY)) === 'true'
}

/**
 * Records that the offer was made — including when the user declined.
 *
 * Declining must leave the app completely empty and must not be asked again.
 */
export async function markPacksOffered(storage: Storage): Promise<void> {
  await storage.settings.set(FIRST_RUN_KEY, 'true')
}

// ─────────────────────────────────────────────────────────────────────────────
// Pack definitions. Data only, from here down.
//
// Seven packs across unrelated subjects, which is the point rather than a
// convenience. One pack about food would read as the app being about food;
// several that share no vocabulary make it obvious that the subject is the
// user's and not the product's.
//
// The first four are all readings of a person, which was a gap worth naming:
// somebody tracking a garden, a skill or a habit could read a list of eating,
// work, exercise and mood and reasonably conclude this was a health app. The
// last three exist to contradict that, and one of them is about something
// entirely outside the user.
//
// Between them they use every shape a track can take — a numeric scale with and
// without a decimal place, a scale with words anchoring what its numbers mean,
// an ordered word scale, an unordered set, several of a set at once, a yes/no
// and a marker — so the possibilities are visible before someone designs their
// own. A shape none of these demonstrates is a shape most users will never
// find.
// ─────────────────────────────────────────────────────────────────────────────

const ONE_TO_TEN: TrackKind = { kind: 'numeric', min: 1, max: 10, step: 1, decimals: 0 }

const MINUTES: TrackKind = { kind: 'numeric', min: 0, max: 240, step: 5, decimals: 0 }

const EATING_PACK: PackDefinition = {
  id: 'eating',
  name: 'Eating',
  description: 'How hunger and fullness move around a meal.',
  tracks: [
    { name: 'Hunger', description: 'How hungry, right now.', kind: ONE_TO_TEN },
    { name: 'Fullness', description: 'How full, right now.', kind: ONE_TO_TEN },
    {
      name: 'Food noise',
      description: 'How much food is on your mind.',
      kind: ONE_TO_TEN,
    },
    {
      name: 'Drank water',
      description: 'One tap. Nothing to rate — only that it happened, and when.',
      kind: { kind: 'marker' },
    },
  ],
  templates: [
    { name: 'Before eating', trackIndices: [0, 2] },
    { name: 'After eating', trackIndices: [1, 2] },
    { name: 'Drink', trackIndices: [3] },
  ],
}

const WORK_PACK: PackDefinition = {
  id: 'work',
  name: 'Study and work',
  description: 'What a working session was like, and where it happened.',
  tracks: [
    { name: 'Focus', description: 'How easily attention stayed put.', kind: ONE_TO_TEN },
    { name: 'Energy', description: 'How much you had to spend.', kind: ONE_TO_TEN },
    {
      name: 'Time spent',
      unit: 'min',
      description: 'How long the session ran.',
      kind: MINUTES,
    },
    {
      name: 'Where',
      description: 'An unordered set — counted and grouped, never averaged.',
      kind: {
        kind: 'enum',
        ordered: false,
        options: [
          { id: 'home', label: 'Home', score: null },
          { id: 'office', label: 'Office', score: null },
          { id: 'library', label: 'Library', score: null },
          { id: 'elsewhere', label: 'Elsewhere', score: null },
        ],
      },
    },
  ],
  templates: [
    { name: 'Starting work', trackIndices: [1, 3] },
    { name: 'Finishing work', trackIndices: [0, 2] },
  ],
}

const MOVEMENT_PACK: PackDefinition = {
  id: 'movement',
  name: 'Exercise and fitness',
  description: 'How hard it felt, how long it took, and how you felt after.',
  tracks: [
    {
      name: 'Effort',
      description: 'An ordered scale — charts and averages like a number.',
      kind: {
        kind: 'enum',
        ordered: true,
        options: [
          { id: 'easy', label: 'Easy', score: 0 },
          { id: 'steady', label: 'Steady', score: 1 },
          { id: 'hard', label: 'Hard', score: 2 },
          { id: 'allOut', label: 'All out', score: 3 },
        ],
      },
    },
    {
      name: 'Duration',
      unit: 'min',
      description: 'How long you moved for.',
      kind: MINUTES,
    },
    {
      name: 'Soreness the next day',
      description: 'Recorded the morning after, not at the time.',
      kind: ONE_TO_TEN,
    },
    {
      name: 'Moved today',
      description: 'A plain yes or no, counted as a rate over time.',
      kind: { kind: 'boolean', trueLabel: 'Yes', falseLabel: 'No' },
    },
  ],
  templates: [
    { name: 'After moving', trackIndices: [0, 1] },
    { name: 'Day check', trackIndices: [3, 2] },
  ],
}

const MIND_AND_BODY_PACK: PackDefinition = {
  id: 'mind-and-body',
  name: 'Mind and body',
  description: 'A morning and an evening reading of how things are.',
  tracks: [
    {
      name: 'Mood',
      description: 'An ordered scale, so it can be charted as a line.',
      kind: {
        kind: 'enum',
        ordered: true,
        options: [
          { id: 'low', label: 'Low', score: 0 },
          { id: 'flat', label: 'Flat', score: 1 },
          { id: 'ok', label: 'OK', score: 2 },
          { id: 'good', label: 'Good', score: 3 },
          { id: 'great', label: 'Great', score: 4 },
        ],
      },
    },
    { name: 'Sleep quality', description: 'How well you slept.', kind: ONE_TO_TEN },
    { name: 'Stress', description: 'How wound up you feel.', kind: ONE_TO_TEN },
    {
      name: 'Rested',
      description: 'Whether you woke up feeling it.',
      kind: { kind: 'boolean', trueLabel: 'Rested', falseLabel: 'Tired' },
    },
  ],
  templates: [
    { name: 'Morning', trackIndices: [3, 1, 0] },
    { name: 'Evening', trackIndices: [0, 2] },
  ],
}

/**
 * Learning something.
 *
 * The purpose is the thing a skill has no score for: whether you are still at
 * it. So there are deliberately **two ways in** — a full session, and a single
 * tap for the day when that is all there is. A tracker that only accepts the
 * full version is one people stop opening on the days it matters most that they
 * did not stop.
 */
const PRACTICE_PACK: PackDefinition = {
  id: 'practice',
  name: 'Learning something',
  description: 'Whether you are still at it, and how the sessions are going.',
  tracks: [
    {
      name: 'Practised',
      description: 'One tap, for the day you have nothing else to give it.',
      kind: { kind: 'marker' },
    },
    {
      name: 'Time at it',
      unit: 'min',
      question: 'How long did you spend?',
      kind: MINUTES,
    },
    {
      name: 'How it went',
      description: 'An ordered scale, so it charts as a line and can be averaged.',
      question: 'How did it go?',
      kind: {
        kind: 'enum',
        ordered: true,
        options: [
          { id: 'frustrating', label: 'Frustrating', score: 0 },
          { id: 'slow', label: 'Slow', score: 1 },
          { id: 'fine', label: 'Fine', score: 2 },
          { id: 'clicked', label: 'It clicked', score: 3 },
        ],
      },
    },
    {
      name: 'What you worked on',
      description: 'An unordered set — counted and grouped, never averaged.',
      question: 'What did you work on?',
      kind: {
        kind: 'enum',
        ordered: false,
        options: [
          { id: 'new', label: 'New material', score: null },
          { id: 'hard', label: 'The hard part', score: null },
          { id: 'review', label: 'Going back over', score: null },
          { id: 'fun', label: 'Just playing', score: null },
        ],
      },
    },
  ],
  templates: [
    {
      name: 'Just practised',
      description: 'One tap, on the days that is all there is.',
      trackIndices: [0],
    },
    {
      name: 'After a session',
      description: 'The fuller version, when there is more to say.',
      trackIndices: [1, 3, 2],
    },
  ],
}

/**
 * Home and garden.
 *
 * The one pack about something other than the person holding the phone, and it
 * is here for that reason as much as its own. It is also the only example with a
 * **decimal** scale: half a millimetre of rain is a real reading, and a whole
 * number would round most of the year away — and the only one with a
 * **multi-select**, because the weather is the least arguable case of several
 * answers being true at once.
 */
const GARDEN_PACK: PackDefinition = {
  id: 'garden',
  name: 'Home and garden',
  description: 'What the weather did, and how the plants are getting on.',
  tracks: [
    {
      name: 'Rainfall',
      unit: 'mm',
      description: 'Half a millimetre at a time — the only example with a decimal.',
      question: 'How much fell?',
      kind: { kind: 'numeric', min: 0, max: 50, step: 0.5, decimals: 1 },
    },
    {
      name: 'Watered',
      description: 'One tap when you have. The gaps are the useful part.',
      kind: { kind: 'marker' },
    },
    {
      name: 'How it is looking',
      question: 'How does it look today?',
      kind: {
        kind: 'enum',
        ordered: true,
        options: [
          { id: 'struggling', label: 'Struggling', score: 0 },
          { id: 'holding', label: 'Holding on', score: 1 },
          { id: 'fine', label: 'Fine', score: 2 },
          { id: 'thriving', label: 'Thriving', score: 3 },
        ],
      },
    },
    {
      name: 'Sky',
      description: 'Choose as many as apply. Each one is counted separately.',
      question: 'What is it doing out there?',
      /*
       * The multi-select (T-1301), and the clearest case for it in these packs:
       * a day is routinely cloudy *and* windy, and a single-choice version
       * makes you pick one and throw the other away. What you get back is a
       * count of each, not a count of every combination.
       */
      kind: {
        kind: 'multi',
        options: [
          { id: 'clear', label: 'Clear', score: null },
          { id: 'cloud', label: 'Cloudy', score: null },
          { id: 'rain', label: 'Rain', score: null },
          { id: 'wind', label: 'Wind', score: null },
          { id: 'frost', label: 'Frost', score: null },
        ],
      },
    },
  ],
  templates: [
    { name: 'Round the garden', trackIndices: [3, 0, 2] },
    { name: 'Watering', trackIndices: [1] },
  ],
}

/**
 * Cutting back.
 *
 * Every other pack points upwards — more focus, more movement, better sleep.
 * This one is the shape for wanting **less** of something, and the design
 * decisions are all about not turning it into a scoreboard.
 *
 * There is no target and no "clean day", because a tracker that grades you is
 * one you stop opening on the days it goes badly — which are the days worth
 * having a record of. The marker counts occurrences and says nothing about
 * whether the count is good.
 *
 * `Rode it out` is the load-bearing idea: it makes the *non*-event recordable in
 * its own right. An urge that came and went leaves no marker, and in a model
 * where absence means absence (ADR-0005) that would make it invisible — so it
 * gets a check-in of its own rather than a "no" on the one above.
 */
const CUTTING_BACK_PACK: PackDefinition = {
  id: 'cutting-back',
  name: 'Cutting back',
  description: 'For something you want less of. No targets, no scoring.',
  tracks: [
    {
      name: 'Had one',
      description: 'One tap. The count over a week is the number, not any one day.',
      kind: { kind: 'marker' },
    },
    {
      name: 'Urge',
      description: 'How strong the pull was — with words naming the ends of the scale.',
      question: 'How strong was the urge?',
      kind: {
        kind: 'numeric',
        min: 1,
        max: 10,
        step: 1,
        decimals: 0,
        // What a 1–10 track is missing without them: only the person who
        // invented the scale can say what 8 on it means.
        words: [
          { at: 1, label: 'Barely there' },
          { at: 5, label: 'Noticeable' },
          { at: 10, label: 'All I can think about' },
        ],
      },
    },
    {
      name: 'What set it off',
      question: 'What set it off?',
      kind: {
        kind: 'enum',
        ordered: false,
        options: [
          { id: 'stress', label: 'Stress', score: null },
          { id: 'habit', label: 'Habit', score: null },
          { id: 'people', label: 'Other people', score: null },
          { id: 'bored', label: 'Boredom', score: null },
          { id: 'unclear', label: 'Nothing obvious', score: null },
        ],
      },
    },
  ],
  templates: [
    { name: 'Had one', trackIndices: [0, 1, 2] },
    {
      name: 'Rode it out',
      description: 'The urge came and nothing followed. Worth a record of its own.',
      trackIndices: [1, 2],
    },
  ],
}

export const examplePacks: readonly PackDefinition[] = [
  EATING_PACK,
  WORK_PACK,
  MOVEMENT_PACK,
  MIND_AND_BODY_PACK,
  PRACTICE_PACK,
  GARDEN_PACK,
  CUTTING_BACK_PACK,
]
